import { useAppDispatch } from 'app/hooks';
import { useContextMenu } from 'components/common/context-menu-manager';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  ContextMenuItemType,
  EdgeDirectionType,
  FootprintPolygonsType,
  GridSpaceDataType,
  PartialWithRequired,
  SpaceType
} from 'data/types';
import {
  beginUndoBatch,
  endUndoBatch,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import React, { useCallback } from 'react';
import { EdgeRunType } from 'utils/opening-position-types';
import { openingRunForEdgeIndex } from 'utils/opening-run-wall';
import {
  assignSpacePolygonShape,
  boundingBoxOfVertices,
  buildOtherSpacePolygonsAbsolute,
  isPolygonInsideAnyFootprint,
  isPolygonSimple,
  polygonArea,
  polygonOverlapsAny,
  RelativeVertex
} from 'utils/space-polygon';
import { GridSpaceContextMenuTarget } from './use-grid-space-context-menu';

type UseGridSpaceVertexEditingParams = {
  gridSpace: GridSpaceDataType;
  allGridSpaces: GridSpaceDataType[];
  effectiveFootprints: FootprintPolygonsType;
  edgeRuns: EdgeRunType[];
  getContextMenuItems: (
    target: GridSpaceContextMenuTarget,
    run?: EdgeRunType
  ) => ContextMenuItemType[];
  contextMenuFilter?: (items: ContextMenuItemType[]) => ContextMenuItemType[];
  setActiveEdge: (edge: EdgeDirectionType | null) => void;
  setActiveSpace: (active: boolean) => void;
  setContextMenuEdgeIndex: (index: number | null) => void;
  setHoveredEdge: (edge: EdgeDirectionType | null) => void;
};

export function useGridSpaceVertexEditing({
  gridSpace,
  allGridSpaces,
  effectiveFootprints,
  edgeRuns,
  getContextMenuItems,
  contextMenuFilter,
  setActiveEdge,
  setActiveSpace,
  setContextMenuEdgeIndex,
  setHoveredEdge
}: UseGridSpaceVertexEditingParams) {
  const dispatch = useAppDispatch();
  const { openContextMenu } = useContextMenu();

  const deleteVertex = useCallback(
    (vertexIndex: number) => {
      const verticesPixels = gridSpace.vertices ?? [];
      if (verticesPixels.length <= 3) return;
      if (vertexIndex < 0 || vertexIndex >= verticesPixels.length) return;

      const verticesFeet = verticesPixels.map((v) => ({
        x: v.x / STRUCTURE_GRID_SIZE,
        y: v.y / STRUCTURE_GRID_SIZE
      }));
      const newVerticesFeet: RelativeVertex[] = verticesFeet.filter(
        (_, i) => i !== vertexIndex
      );

      if (!isPolygonSimple(newVerticesFeet)) return;

      const positionFeet = {
        x: gridSpace.position.x / STRUCTURE_GRID_SIZE,
        y: gridSpace.position.y / STRUCTURE_GRID_SIZE
      };
      const absVerticesFeet = newVerticesFeet.map((v) => ({
        x: positionFeet.x + v.x,
        y: positionFeet.y + v.y
      }));

      if (!isPolygonInsideAnyFootprint(absVerticesFeet, effectiveFootprints)) {
        return;
      }
      const otherPolys = buildOtherSpacePolygonsAbsolute(
        allGridSpaces,
        gridSpace.spaceId
      );
      if (polygonOverlapsAny(absVerticesFeet, otherPolys)) return;

      const bbBefore = boundingBoxOfVertices(newVerticesFeet);
      const newPositionFeet = {
        x: positionFeet.x + bbBefore.minX,
        y: positionFeet.y + bbBefore.minY
      };

      const draft: {
        vertices: RelativeVertex[];
        width?: number;
        length?: number;
      } = { vertices: newVerticesFeet };
      assignSpacePolygonShape(draft, newVerticesFeet);

      dispatch(beginUndoBatch());
      try {
        dispatch(
          updateStructureItem({
            uniqueId: gridSpace.spaceId,
            position: newPositionFeet,
            width: draft.width,
            length: draft.length,
            vertices: draft.vertices
          } as PartialWithRequired<SpaceType, 'uniqueId'>)
        );
        dispatch(
          updateStructureItemAttribute({
            uniqueId: gridSpace.spaceId,
            attributeCode: 'square_footage',
            value: Math.round(polygonArea(draft.vertices ?? newVerticesFeet))
          })
        );
      } finally {
        dispatch(endUndoBatch());
      }
    },
    [
      dispatch,
      gridSpace.spaceId,
      gridSpace.vertices,
      gridSpace.position.x,
      gridSpace.position.y,
      allGridSpaces,
      effectiveFootprints
    ]
  );

  const handleVertexContextMenu = useCallback(
    (e: React.MouseEvent, vertexIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      const vertices = gridSpace.vertices ?? [];
      const canDelete = vertices.length > 3;
      const contextMenuItems: ContextMenuItemType[] = [
        {
          label: 'Delete Vertex',
          onClick: () => deleteVertex(vertexIndex),
          disabled: !canDelete
        }
      ];
      openContextMenu(
        e.clientX,
        e.clientY,
        contextMenuItems,
        `${gridSpace.id}-vertex-${vertexIndex}`
      );
    },
    [openContextMenu, gridSpace.id, gridSpace.vertices, deleteVertex]
  );

  const addVertex = useCallback(
    (edgeIndex: number) => {
      const verticesPixels = gridSpace.vertices ?? [];
      if (verticesPixels.length < 3) return;
      if (edgeIndex < 0 || edgeIndex >= verticesPixels.length) return;

      const verticesFeet = verticesPixels.map((v) => ({
        x: v.x / STRUCTURE_GRID_SIZE,
        y: v.y / STRUCTURE_GRID_SIZE
      }));
      const p1 = verticesFeet[edgeIndex];
      const p2 = verticesFeet[(edgeIndex + 1) % verticesFeet.length];
      const midpoint: RelativeVertex = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2
      };
      const newVerticesFeet: RelativeVertex[] = [
        ...verticesFeet.slice(0, edgeIndex + 1),
        midpoint,
        ...verticesFeet.slice(edgeIndex + 1)
      ];

      if (!isPolygonSimple(newVerticesFeet)) return;

      const draft: {
        vertices: RelativeVertex[];
        width?: number;
        length?: number;
      } = { vertices: newVerticesFeet };
      assignSpacePolygonShape(draft, newVerticesFeet);

      dispatch(beginUndoBatch());
      try {
        dispatch(
          updateStructureItem({
            uniqueId: gridSpace.spaceId,
            width: draft.width,
            length: draft.length,
            vertices: draft.vertices
          } as PartialWithRequired<SpaceType, 'uniqueId'>)
        );
        dispatch(
          updateStructureItemAttribute({
            uniqueId: gridSpace.spaceId,
            attributeCode: 'square_footage',
            value: Math.round(polygonArea(draft.vertices ?? newVerticesFeet))
          })
        );
      } finally {
        dispatch(endUndoBatch());
      }
    },
    [dispatch, gridSpace.spaceId, gridSpace.vertices]
  );

  const handleEdgeContextMenu = useCallback(
    (
      e: React.MouseEvent,
      edgeIndex: number,
      cardinal: EdgeDirectionType | null
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const run = openingRunForEdgeIndex(edgeRuns, edgeIndex);
      const target = run?.edge ?? cardinal;
      const baseItems: ContextMenuItemType[] = target
        ? getContextMenuItems(target, run)
        : [];
      const addVertexItem: ContextMenuItemType = {
        label: 'Add Vertex',
        onClick: () => addVertex(edgeIndex)
      };
      let menuItems: ContextMenuItemType[] =
        baseItems.length > 0
          ? [...baseItems, { separator: true }, addVertexItem]
          : [addVertexItem];

      if (contextMenuFilter) {
        menuItems = contextMenuFilter(menuItems);
      }

      if (menuItems.length === 0) return;

      setActiveEdge(target);
      setActiveSpace(false);
      setContextMenuEdgeIndex(edgeIndex);

      openContextMenu(
        e.clientX,
        e.clientY,
        menuItems,
        `${gridSpace.id}-edge-${edgeIndex}`
      );
    },
    [
      openContextMenu,
      gridSpace.id,
      edgeRuns,
      getContextMenuItems,
      contextMenuFilter,
      addVertex,
      setActiveEdge,
      setActiveSpace,
      setContextMenuEdgeIndex
    ]
  );

  const handleEdgeHover = useCallback(
    (edge: EdgeDirectionType | null) => {
      setHoveredEdge(edge);
    },
    [setHoveredEdge]
  );

  return {
    handleVertexContextMenu,
    handleEdgeContextMenu,
    handleEdgeHover
  };
}
